import React, { useRef, useEffect } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { api } from '../../axiosConfig';

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_DISABLE_CAPTCHA === 'true'
    ? ''
    : process.env.REACT_APP_RECAPTCHA_SITE_KEY || '';

const GoogleReCaptcha = ({  onCaptchaValidation }) => {
    const recaptchaRef = useRef<any>(null);

    useEffect(() => {
        // Missing-key guard: if REACT_APP_RECAPTCHA_SITE_KEY is not set, skip
        // the widget entirely and report success immediately so the consumer's
        // submit button isn't permanently disabled. Covers local dev that hasn't
        // set the key and non-IN portals that don't use captcha.
        if (!RECAPTCHA_SITE_KEY) {
            console.warn('[reCAPTCHA] Site key missing — skipping captcha validation');
            if (onCaptchaValidation) {
                onCaptchaValidation({ success: true });
            }
            return;
        }
        if (recaptchaRef.current) {
            recaptchaRef.current.execute();
        }
    }, []);

    // Google's challenge iframe (and its wrapper divs) can get stuck with
    // pointer-events:none while it transitions from the invisible badge
    // state, leaving the challenge tiles unclickable. CSS alone can't
    // reliably reach every ancestor cross-browser, so watch for the
    // challenge iframe and force pointer-events:auto up the ancestor chain
    // whenever it appears.
    useEffect(() => {
        const fix = () => {
            document.querySelectorAll('iframe[title*="recaptcha challenge"]').forEach(f => {
                let el = f;
                while (el) {
                    el.style.pointerEvents = 'auto';
                    el = el.parentElement;
                }
            });
        };
        const observer = new MutationObserver(fix);
        observer.observe(document.body, { childList: true, subtree: true });
        return () => observer.disconnect();
    }, []);

    const handleToken = token => {
        api.post('/', { recaptchaToken: token })
      .then(res => {
          onCaptchaValidation(res.data)
      })
      .catch((error) => {
          console.error('Error:', error);
          onCaptchaValidation({ success: true });
      });
    };

    const onReCAPTCHAChange = token => {
        if (token && onCaptchaValidation) {
            handleToken(token);
        }
    };

    // Don't mount the widget when the key is missing — react-google-recaptcha
    // crashes/misbehaves with an empty sitekey. The useEffect above has already
    // notified the consumer via onCaptchaValidation({success:true}).
    if (!RECAPTCHA_SITE_KEY) {
        return null;
    }

    return (
        <ReCAPTCHA
            ref={recaptchaRef}
            sitekey={RECAPTCHA_SITE_KEY}
            size="invisible"
            onChange={onReCAPTCHAChange}
        />
    );
};

export default GoogleReCaptcha;
